import { ChevronLeft, ChevronRight, Icon as TablerIcon } from 'tabler-icons-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Collapse, Divider, Group, Text } from '@mantine/core';
import useNavbarItemStyles from './useNavbarItemStyles';
import { useIntlLight } from '../../../i18n';

export interface NavbarItemProps {
  icon: TablerIcon;
  label: string;
  links?: { label: string; link: string; icon?: TablerIcon }[];
  link?: string;
  handlers?(): any;
  isCollapsible?: boolean;
  isInitiallyCollapsed?: boolean;
}

export default function NavbarItem({
  icon: Icon,
  label,
  links,
  link,
  handlers,
  isCollapsible = false,
  isInitiallyCollapsed = true,
}: NavbarItemProps) {
  const { t } = useIntlLight();
  const { classes, theme } = useNavbarItemStyles();
  const [expanded, setExpanded] = useState(!isInitiallyCollapsed);
  const ChevronIcon = theme.dir === 'ltr' ? ChevronRight : ChevronLeft;

  const subNavItems = links
    ? links.map(item => {
        if (item.link === undefined) {
          return item.label ? (
            <Divider
              py={7}
              px={8}
              label={<Text sx={{ color: 'white !important' }}>{t(item.label)}</Text>}
              key={item.label}
              labelPosition="center"
              color="white"
            />
          ) : (
            <Box key={item.label} py={16} px={8}>
              <Divider color="white" />
            </Box>
          );
        }

        const MenuIcon = item.icon;

        return (
          <NavLink
            className={({ isActive }) => `${classes.navLink} ${isActive ? classes.linkActive : ''}`}
            to={item.link}
            key={item.label}
            onClick={handlers}
          >
            <Group position="apart" spacing={0}>
              <Box sx={{ display: 'flex', alignItems: 'center' }} ml={8}>
                {MenuIcon && <MenuIcon size={20} />}
                <Box mx={8}>{t(item.label)}</Box>
              </Box>
            </Group>
          </NavLink>
        );
      })
    : null;

  return (
    <Box className={classes.navContainer}>
      {link ? (
        <NavLink
          className={({ isActive }) => `${classes.navLink} ${isActive ? classes.linkActive : ''}`}
          to={link}
          key={label}
          onClick={handlers}
        >
          <Group position="apart" spacing={0}>
            <Box sx={{ display: 'flex', alignItems: 'center' }} ml={8}>
              <Icon size={20} />
              <Box mx={8}>{t(label)}</Box>
            </Box>
          </Group>
        </NavLink>
      ) : (
        <Group
          className={isCollapsible ? classes.navMenuCollapse : classes.navMenu}
          position="apart"
          spacing={0}
          onClick={isCollapsible ? () => setExpanded(o => !o) : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }} ml={8}>
            <Icon size={20} />
            <Box mx={8}>{t(label)}</Box>
          </Box>
          {isCollapsible && (
            <ChevronIcon
              className={classes.chevron}
              size={14}
              style={{
                transform: expanded ? `rotate(${theme.dir === 'rtl' ? -90 : 90}deg)` : 'none',
              }}
            />
          )}
        </Group>
      )}
      {subNavItems && (
        <Box className={classes.subNavContainer}>
          {isCollapsible ? <Collapse in={expanded}>{subNavItems}</Collapse> : subNavItems}
        </Box>
      )}
    </Box>
  );
}
